/**
 * The people, teams and ICS positions an action can be assigned to.
 *
 * Extracted from `useScopedActionForm` because the table needs the same lists
 * the drawer does, for the opposite purpose: the drawer offers them as choices,
 * the table resolves an `assignee_id` back to a name. Reading `poc_user_name`
 * alone is not enough, since coms deliberately clears `poc` for team and
 * position assignments, which made every one of those read as "Unassigned".
 */

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  selectGroupGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { useActualIncidentId } from './commonHooks';
import { fetchRostersForGroups } from '../../../actions/roleActions';
import { IapScope, INCIDENT } from '../ICSConstants';
import { useGetPositionsForIncidentId } from 'hooks/incident/useRoster';
import {
  incidentRosterQueryOptions,
  useIncidentRoster,
} from './incidentRoster/useIncidentRoster';
import { useStrikeTeams } from './strikeTeamHooks';
import type { AssigneeDirectory } from '../actionsTable/actionEditModel';

export const useActionAssigneeDirectory = (
  scope: IapScope,
  incidentIdOverride?: string | null
): AssigneeDirectory => {
  const isIncident = scope === INCIDENT;
  const dispatch = useDispatch<any>();

  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride || routeIncidentId;
  const rosterFromRoute = useIncidentRoster();
  const rosterFromOverride = useQuery({
    ...incidentRosterQueryOptions(incidentId ?? ''),
    enabled: isIncident && !!incidentIdOverride && !!incidentId,
  });
  const incidentRoster = incidentIdOverride
    ? rosterFromOverride.data
    : rosterFromRoute.data;
  const { data: strikeTeams } = useStrikeTeams();

  const groupGuid = useAppSelector(selectGroupGuid);
  const rosters = useAppSelector(
    (state: any) => (state.app.rostersForGroups as RosterItem[]) || []
  );

  useEffect(() => {
    if (!isIncident && groupGuid && rosters.length === 0) {
      dispatch(fetchRostersForGroups([groupGuid]));
    }
  }, [dispatch, isIncident, groupGuid, rosters.length]);

  const users = useMemo(() => {
    if (isIncident) {
      return (incidentRoster ?? [])
        .filter((u) => u.userName || u.emailAddress)
        .map((u) => ({
          label: u.userName ?? (u.emailAddress as string),
          value: u.userId,
        }));
    }
    return rosters
      .filter((r) => r.group_guid === groupGuid)
      .map((r) => ({ label: r.user_name, value: r.user_guid }));
  }, [isIncident, incidentRoster, rosters, groupGuid]);

  /**
   * Every seat on this incident, including ones nobody has filled yet. Walking
   * only the people on the roster drops vacant seats, and the ICS Position
   * select then renders the stored id. Org-scope actions have no roster, so
   * they stay user-assigned.
   */
  const incidentPositionsQuery = useGetPositionsForIncidentId(
    isIncident && incidentId ? incidentId : ''
  );
  const positions = useMemo(() => {
    if (!isIncident) return [];
    return (incidentPositionsQuery.data ?? [])
      .filter((position) => position.id && position.name)
      .map((position) => ({ label: position.name, value: position.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isIncident, incidentPositionsQuery.data]);

  const teams = useMemo(() => {
    if (!isIncident) return [];
    return (strikeTeams ?? [])
      .filter((team) => !team.archived && team.name)
      .map((team) => ({ label: team.name, value: team.strike_team_id }));
  }, [isIncident, strikeTeams]);

  return useMemo(
    () => ({ users, teams, positions, roster: incidentRoster ?? [] }),
    [incidentRoster, users, teams, positions]
  );
};
